import { Check, Trash2 } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import { useState } from 'react';
import { Alert, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Purchase } from '../../service/api';
import Coin from '../Coin';

interface ManagerPurchaseCardProps {
  purchase: Purchase;
  onApprove: (purchaseId: string) => Promise<void>;
  onDelete: (purchaseId: string) => Promise<void>;
}

export default function ManagerPurchaseCard({
  purchase,
  onApprove,
  onDelete,
}: ManagerPurchaseCardProps) {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      await onApprove(purchase._id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('manager.purchases.confirmDelete'))) return;

    setIsProcessing(true);
    setError(null);
    try {
      await onDelete(purchase._id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setIsProcessing(false);
    }
  };

  const isPending = purchase.status === 'Pending';
  const isApproved = purchase.status === 'Approved';

  return (
    <Card className={`mb-3 shadow-sm border ${!isPending ? 'opacity-75' : ''}`}>
      <Card.Body>
        <Row className="align-items-center mb-3">
          <Col xs="auto">
            <DynamicIcon
              name={purchase.item.icon}
              size={32}
              className="text-primary"
            />
          </Col>
          <Col>
            <h6 className="mb-1">
              {purchase.item.title} × {purchase.quantity}
            </h6>
            <p className="text-muted small mb-1">
              {t('manager.purchases.requestedBy')}:{' '}
              {purchase.requestedBy.given_name}{' '}
              {purchase.requestedBy.family_name}
            </p>
            <p className="text-muted small mb-0">
              {t('manager.purchases.requestedAt')}:{' '}
              {purchase.createdAt.toFormat('MMM d, h:mm a')}
            </p>
            {isApproved && purchase.processedAt && (
              <p className="text-success small mb-0">
                {t('manager.purchases.approvedAt')}:{' '}
                {purchase.processedAt.toFormat('MMM d, h:mm a')}
              </p>
            )}
          </Col>
          <Col xs="auto" className="d-flex align-items-center gap-2">
            <span className="fw-bold">{purchase.totalAmount}</span>
            <Coin size="sm" />
          </Col>
        </Row>

        {isPending && (
          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="success"
              size="sm"
              onClick={handleApprove}
              disabled={isProcessing}
              className="d-flex align-items-center gap-1"
            >
              {isProcessing ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <Check size={16} />
                  {t('manager.purchases.approve')}
                </>
              )}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              disabled={isProcessing}
              className="d-flex align-items-center gap-1"
            >
              {isProcessing ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <Trash2 size={16} />
                  {t('manager.purchases.delete')}
                </>
              )}
            </Button>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mt-3 mb-0 py-2">
            {error}
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}
